import { styled } from '@linaria/react';
import { Group, Progress, Stack, Table } from '@mantine/core';
import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router';
import type { Outcome } from '../../../../api/strategy-client/strategy-client.type';
import { useTranslation } from 'react-i18next';
import { formatDate } from '../../../../helpers/date-time-helpers/formatting/formatting';
import { useGlobalStore } from '../../../../store/global-store/global-store';
import {
  inkLight,
  primaryBase,
  redBase,
  secondaryBase,
  skyDark,
  skyLight,
  skyLightest,
  yellowBase,
} from '../../../../styles/design-tokens';
import { Icon } from '../../../../ui-library/icon/icon';
import { Tooltip } from '../../../../ui-library/tooltip/tooltip';
import { Text } from '../../../../ui-library/typography/typography';
import { getRiskCategory } from '../../../adherence/risk-analysis/risk-analysis.helpers';
import { Label } from '../../initiative-list/filters/status-label';
import { getRelativeEndDate } from '../overview.helpers';
import { getLatestCount, getStatusColor, getStatusLabel } from './simplified-initiatives-table-helper';

type SimplifiedOutcomesTableProps = {
  outcomes: Outcome[];
};

type SortField = 'name' | 'progress' | 'riskScore' | 'status' | 'endDate';
type SortDirection = 'asc' | 'desc';

export function SimplifiedOutcomesTable({ outcomes }: SimplifiedOutcomesTableProps) {
  const i18n = useTranslation();
  const useAI = useGlobalStore((state) => state.useAI);
  const [sortField, setSortField] = useState<SortField>('name');
  const [sortDirection, setSortDirection] = useState<SortDirection>('asc');
  const navigate = useNavigate();

  const sortedOutcomes = useMemo(() => {
    if (!outcomes || outcomes.length === 0) return [];

    return [...outcomes].sort((a, b) => {
      let aValue: string | number;
      let bValue: string | number;

      switch (sortField) {
        case 'name':
          aValue = a.name.toLowerCase();
          bValue = b.name.toLowerCase();
          break;
        case 'progress':
          aValue = a.percent_progress ?? 0;
          bValue = b.percent_progress ?? 0;
          break;
        case 'riskScore':
          aValue = a.flora_annotations?.risk_score ?? -1;
          bValue = b.flora_annotations?.risk_score ?? -1;
          break;
        case 'status':
          aValue = a.status?.by_tasks?.status || 'unknown';
          bValue = b.status?.by_tasks?.status || 'unknown';
          break;
        case 'endDate':
          aValue = a.end_date ? new Date(a.end_date).getTime() : 0;
          bValue = b.end_date ? new Date(b.end_date).getTime() : 0;
          break;
        default:
          return 0;
      }

      if (aValue < bValue) return sortDirection === 'asc' ? -1 : 1;
      if (aValue > bValue) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    });
  }, [outcomes, sortField, sortDirection]);

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const renderSortIcon = (field: SortField) => {
    if (sortField !== field) {
      return <Icon name="unfold_more" size={14} color={inkLight} />;
    }
    return sortDirection === 'asc' ? (
      <Icon name="expand_less" size={14} color={secondaryBase} />
    ) : (
      <Icon name="expand_more" size={14} color={secondaryBase} />
    );
  };

  if (!outcomes || outcomes.length === 0) {
    return (
      <Text size="small" color="#666" style={{ padding: '16px', textAlign: 'center' }}>
        {i18n.t('strategy.overview.no_outcomes')}
      </Text>
    );
  }

  return (
    <TableContainer>
      <Table
        verticalSpacing="xs"
        horizontalSpacing="sm"
        withRowBorders
        style={{
          width: '100%',
          fontSize: '13px',
          tableLayout: 'fixed',
        }}
      >
        <Table.Thead
          style={{
            backgroundColor: skyLightest,
            position: 'sticky',
            top: 0,
            zIndex: 20,
            borderBottom: `1px solid ${skyLight}`,
          }}
        >
          <Table.Tr>
            <Table.Th style={{ width: useAI ? '40%' : '50%' }}>
              <SortableHeader onClick={() => handleSort('name')}>
                <Text color={inkLight} size="tiny" weight="medium">
                  {i18n.t('common.outcome').toUpperCase()}
                </Text>
                {renderSortIcon('name')}
              </SortableHeader>
            </Table.Th>
            <Table.Th style={{ width: useAI ? '18%' : '25%' }}>
              <SortableHeader $centered onClick={() => handleSort('progress')}>
                <Text color={inkLight} size="tiny" weight="medium">
                  PROGRESS
                </Text>
                {renderSortIcon('progress')}
              </SortableHeader>
            </Table.Th>
            {useAI && (
              <Table.Th style={{ width: '14%' }}>
                <SortableHeader $centered onClick={() => handleSort('riskScore')}>
                  <Text color={inkLight} size="tiny" weight="medium">
                    RISK
                  </Text>
                  {renderSortIcon('riskScore')}
                </SortableHeader>
              </Table.Th>
            )}
            <Table.Th style={{ width: useAI ? '14%' : '25%' }}>
              <SortableHeader $centered onClick={() => handleSort('endDate')}>
                <Text color={inkLight} size="tiny" weight="medium">
                  END DATE
                </Text>
                {renderSortIcon('endDate')}
              </SortableHeader>
            </Table.Th>
          </Table.Tr>
        </Table.Thead>
        <Table.Tbody>
          {sortedOutcomes.map((outcome) => {
            const riskCategory = getRiskCategory(outcome.flora_annotations?.risk_score);
            const riskScore = outcome.flora_annotations?.risk_score ?? null;
            const completionPercent = Math.round(outcome.percent_progress || 0);
            const progressColor =
              completionPercent >= 75
                ? primaryBase
                : completionPercent >= 50
                  ? yellowBase
                  : completionPercent >= 25
                    ? '#FF9800'
                    : redBase;

            return (
              <Table.Tr key={outcome.id}>
                {/* Outcome name with status bubble */}
                <Table.Td>
                  <Group gap={8} wrap="nowrap" align="flex-start">
                    <Tooltip variant="black" label={getStatusLabel(outcome.status?.by_tasks?.status)} zIndex={1000}>
                      <div style={{ paddingTop: '2px', marginTop: '5px' }}>
                        <Label color={getStatusColor(outcome.status?.by_tasks?.status)} />
                      </div>
                    </Tooltip>
                    <Stack gap={2} style={{ flex: 1, minWidth: 0 }}>
                      <Tooltip variant="black" label={outcome.name} zIndex={1000}>
                        <Text
                          onClick={() => navigate(`/application/strategy/overview?outcomeId=${outcome.id}`)}
                          size="small"
                          weight="medium"
                          style={{
                            textOverflow: 'ellipsis',
                            overflow: 'hidden',
                            whiteSpace: 'nowrap',
                            cursor: 'pointer',
                          }}
                        >
                          {outcome.name}
                        </Text>
                      </Tooltip>
                    </Stack>
                  </Group>
                </Table.Td>

                {/* Progress bar with percentage */}
                <Table.Td style={{ textAlign: 'center' }}>
                  <Tooltip
                    variant="black"
                    label={
                      <Stack gap={2}>
                        <Text size="medium" weight="medium">
                          Progress
                        </Text>
                        {(() => {
                          const completedTasks = getLatestCount(outcome.completed_subtasks_over_time?.tasks);
                          const totalTasks = getLatestCount(outcome.total_subtasks_over_time?.tasks);
                          const completedPoints = getLatestCount(outcome.completed_subtasks_over_time?.points);
                          const totalPoints = getLatestCount(outcome.total_subtasks_over_time?.points);

                          return (
                            <>
                              {completedTasks !== null && totalTasks !== null && (
                                <Text size="small">
                                  Tasks: {completedTasks} / {totalTasks}
                                </Text>
                              )}
                              {completedPoints !== null && totalPoints !== null && (
                                <Text size="small">
                                  Points: {completedPoints} / {totalPoints}
                                </Text>
                              )}
                            </>
                          );
                        })()}
                      </Stack>
                    }
                    zIndex={1000}
                  >
                    <Group gap={4} align="center" wrap="nowrap" style={{ justifyContent: 'center' }}>
                      <Progress
                        value={completionPercent}
                        color={progressColor}
                        size="xs"
                        radius="xl"
                        style={{ width: '60px', flexShrink: 0 }}
                      />
                      <Text size="tiny" weight="medium" style={{ whiteSpace: 'nowrap' }}>
                        {completionPercent}%
                      </Text>
                    </Group>
                  </Tooltip>
                </Table.Td>

                {/* Risk score */}
                {useAI && (
                  <Table.Td style={{ textAlign: 'center' }}>
                    {riskScore !== null ? (
                      <Tooltip
                        variant="black"
                        label={
                          <Stack gap={2}>
                            <Text size="medium" weight="medium">
                              Risk Summary
                            </Text>
                            <Text size="small">{outcome.flora_annotations?.executive_summary}</Text>
                          </Stack>
                        }
                        maw={450}
                        multiline
                        zIndex={1000}
                      >
                        <RiskBadge $color={riskCategory.color}>
                          <Text size="tiny" weight="bold">
                            {riskScore.toFixed(0)}
                          </Text>
                        </RiskBadge>
                      </Tooltip>
                    ) : (
                      <Text size="tiny" color={skyDark}>
                        N/A
                      </Text>
                    )}
                  </Table.Td>
                )}

                {/* End date in MM/DD/YY format */}
                <Table.Td style={{ textAlign: 'center' }}>
                  <Tooltip variant="black" label={getRelativeEndDate(outcome.end_date)} zIndex={1000}>
                    <Text size="tiny" weight="medium">
                      {formatDate(outcome.end_date, { fallback: 'N/A', format: 'MM/DD/YY' })}
                    </Text>
                  </Tooltip>
                </Table.Td>
              </Table.Tr>
            );
          })}
        </Table.Tbody>
      </Table>
    </TableContainer>
  );
}

const TableContainer = styled.div`
  width: 100%;
  border-radius: 12px;
  overflow-x: auto;
  overflow-y: hidden;
  border: 1px solid ${skyLight};
  background-color: white;
`;

const SortableHeader = styled.div<{ $centered?: boolean }>`
  display: flex;
  align-items: center;
  gap: 4px;
  cursor: pointer;
  user-select: none;
  justify-content: ${(props) => (props.$centered ? 'center' : 'flex-start')};

  &:hover {
    opacity: 0.8;
  }
`;

const RiskBadge = styled.div<{ $color: string }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 3px 8px;
  border-radius: 10px;
  background-color: ${(props) => `${props.$color}20`};
  border: 1px solid ${(props) => props.$color};
  min-width: 32px;
`;
